class LeadCapturePopup {
  constructor() {
    this.popup = document.getElementById('lead-capture-popup');
    this.form = document.getElementById('lead-capture-form');
    this.closeButtons = document.querySelectorAll('[data-lead-capture-close]');
    this.recallButton = document.getElementById('lead-capture-recall');
    this.cookieName = 'lead_capture_shown';
    this.cookieDismissedName = 'lead_capture_dismissed';
    this.sessionShownKey = 'lead_capture_session_shown';
    
    this.init();
  }
  
  init() {
    if (!this.popup) return;
    
    this.bindEvents();
    this.checkTriggerConditions();
  }
  
  bindEvents() {
    this.closeButtons.forEach(button => {
      button.addEventListener('click', () => this.closePopup(true));
    });
    
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.popup.classList.contains('active')) {
        this.closePopup(true);
      }
    });
    
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }
    
    const linkButton = document.querySelector('[data-lead-capture-link]');
    if (linkButton) {
      linkButton.addEventListener('click', (e) => this.handleLinkClick(e));
    }
    
    // Recall button event
    if (this.recallButton) {
      this.recallButton.querySelector('.recall-button').addEventListener('click', () => this.showPopupFromRecall());
    }
  }
  
  checkTriggerConditions() {
    const settings = window.leadCaptureSettings || {};
    
    if (this.hasBeenDismissed()) return;
    
    if (sessionStorage.getItem(this.sessionShownKey) && !settings.show_every_session) {
      return;
    }
    
    if (this.getCookie(this.cookieName) && !settings.ignore_cookie) {
      return;
    }
    
    switch(settings.trigger_type) {
      case 'time_delay':
        this.triggerByTime(settings.trigger_delay || 20);
        break;
      case 'scroll_percentage':
        this.triggerByScroll(settings.scroll_percentage || 50);
        break;
      case 'exit_intent':
        this.triggerByExitIntent();
        break;
      case 'page_views':
        this.triggerByPageViews(settings.page_view_count || 3);
        break;
      case 'immediate':
        this.showPopup();
        break;
      default:
        this.triggerByTime(20);
    }
  }
  
  triggerByTime(seconds) {
    setTimeout(() => {
      if (!this.popup.classList.contains('active')) {
        this.showPopup();
      }
    }, seconds * 1000);
  }
  
  triggerByScroll(percentage) {
    let triggered = false;
    
    const checkScroll = () => {
      if (triggered) return;
      
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = (window.scrollY / scrollHeight) * 100;
      
      if (scrolled >= percentage) {
        triggered = true;
        this.showPopup();
      }
    };
    
    window.addEventListener('scroll', checkScroll, { passive: true });
  }
  
  triggerByExitIntent() {
    let triggered = false;
    
    document.addEventListener('mouseout', (e) => {
      if (triggered) return;
      
      if (!e.toElement && !e.relatedTarget && e.clientY < 10) {
        triggered = true;
        this.showPopup();
      }
    });
    
    if ('ontouchstart' in window) {
      let lastScrollY = window.scrollY;
      let lastScrollTime = Date.now();
      
      window.addEventListener('scroll', () => {
        const currentScrollY = window.scrollY;
        const currentTime = Date.now();
        const scrollSpeed = Math.abs(currentScrollY - lastScrollY) / (currentTime - lastScrollTime);
        
        if (currentScrollY < lastScrollY && scrollSpeed > 0.5 && currentScrollY < 100) {
          if (!triggered) {
            triggered = true;
            this.showPopup();
          }
        }
        
        lastScrollY = currentScrollY;
        lastScrollTime = currentTime;
      }, { passive: true });
    }
  }
  
  triggerByPageViews(count) {
    let views = parseInt(sessionStorage.getItem('lead_capture_page_views') || '0');
    views++;
    sessionStorage.setItem('lead_capture_page_views', views.toString());
    
    if (views >= count) {
      this.showPopup();
      sessionStorage.removeItem('lead_capture_page_views');
    }
  }
  
  showPopup() {
    if (this.popup.classList.contains('active')) return;
    
    this.popup.classList.add('active');
    this.popup.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    
    // Hide recall button when popup is shown
    this.hideRecallButton();
    
    this.setCookie(this.cookieName, 'true', 30);
    sessionStorage.setItem(this.sessionShownKey, 'true');
    
    const firstInput = this.form?.querySelector('input:not([type="hidden"])');
    if (firstInput) {
      setTimeout(() => firstInput.focus(), 100);
    }
    
    if (window.leadCaptureSettings?.analytics_enabled) {
      this.trackEvent('lead_capture_shown');
    }
  }
  
  closePopup(isDismissed = false) {
    this.popup.classList.remove('active');
    this.popup.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    
    if (isDismissed) {
      const settings = window.leadCaptureSettings || {};
      const dismissDays = settings.dismiss_cookie_days || 7;
      this.setCookie(this.cookieDismissedName, 'true', dismissDays);
      
      // Show recall button when dismissed (not on form submission)
      if (settings.show_recall_button) {
        this.showRecallButton();
      }
      
      if (settings.analytics_enabled) {
        this.trackEvent('lead_capture_dismissed');
      }
    }
  }
  
  async handleSubmit(e) {
    e.preventDefault();
    
    const form = e.target;
    const submitButton = form.querySelector('.lead-capture-submit');
    const successMessage = form.querySelector('.form-message--success');
    const errorMessage = form.querySelector('.form-message--error');
    
    form.classList.add('loading');
    submitButton.disabled = true;
    
    successMessage.style.display = 'none';
    errorMessage.style.display = 'none';
    
    try {
      const formData = new FormData(form);
      const response = await fetch(form.action, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        successMessage.style.display = 'flex';
        form.reset();
        
        const settings = window.leadCaptureSettings || {};
        this.setCookie(this.cookieDismissedName, 'true', settings.success_cookie_days || 365);
        
        if (settings.analytics_enabled) {
          this.trackEvent('lead_capture_submitted', {
            email: formData.get('contact[email]')
          });
        }
        
        if (settings.auto_close_on_success) {
          setTimeout(() => this.closePopup(), (settings.auto_close_delay || 3) * 1000);
        }
        
        if (settings.redirect_url) {
          setTimeout(() => {
            window.location.href = settings.redirect_url;
          }, 1000);
        }
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      errorMessage.textContent = window.leadCaptureSettings?.error_message || 'Something went wrong. Please try again.';
      errorMessage.style.display = 'flex';
      
      if (window.leadCaptureSettings?.analytics_enabled) {
        this.trackEvent('lead_capture_error', { error: error.message });
      }
    } finally {
      form.classList.remove('loading');
      submitButton.disabled = false;
    }
  }
  
  setCookie(name, value, days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
  }
  
  getCookie(name) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }
  
  hasBeenDismissed() {
    return this.getCookie(this.cookieDismissedName) === 'true';
  }
  
  handleLinkClick(e) {
    const settings = window.leadCaptureSettings || {};
    
    this.setCookie(this.cookieDismissedName, 'true', settings.success_cookie_days || 360);
    
    if (settings.analytics_enabled) {
      this.trackEvent('lead_capture_link_clicked', {
        url: e.currentTarget.href
      });
    }
    
    setTimeout(() => {
      this.closePopup();
    }, 100);
  }
  
  trackEvent(eventName, data = {}) {
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, data);
    }
    
    if (typeof window.fbq === 'function' && (eventName === 'lead_capture_submitted' || eventName === 'lead_capture_link_clicked')) {
      window.fbq('track', 'Lead', data);
    }
    
    if (window.Shopify && window.Shopify.analytics) {
      window.Shopify.analytics.publish(eventName, data);
    }
  }
  
  showRecallButton() {
    if (this.recallButton) {
      setTimeout(() => {
        this.recallButton.classList.add('active');
        this.recallButton.setAttribute('aria-hidden', 'false');
      }, 300); // Delay to allow popup close animation to complete
    }
  }
  
  hideRecallButton() {
    if (this.recallButton) {
      this.recallButton.classList.remove('active');
      this.recallButton.setAttribute('aria-hidden', 'true');
    }
  }
  
  showPopupFromRecall() {
    // Clear the dismissed cookie to allow popup to show again
    this.deleteCookie(this.cookieDismissedName);
    sessionStorage.removeItem(this.sessionShownKey);
    
    // Show the popup
    this.showPopup();
    
    const settings = window.leadCaptureSettings || {};
    if (settings.analytics_enabled) {
      this.trackEvent('lead_capture_reopened_from_recall');
    }
  }
  
  deleteCookie(name) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (window.leadCaptureSettings?.enabled !== false) {
    new LeadCapturePopup();
  }
});

window.LeadCapturePopup = LeadCapturePopup;